/**
 * Helpers partagés — thème CALCUL LITTÉRAL 3ème (attendus 208-212, d12).
 *
 * Arithmétique rationnelle entière exacte. Les expressions littérales sont
 * produites sous forme de FRAGMENTS LaTeX (sans les `$`), à emballer par
 * `tex()` au moment de l'insertion — voir FORMATS_TANOED §0.5bis. Les trous ne
 * contiennent QUE des nombres isolés, tapés au clavier en ASCII (« -5 »,
 * « 3/4 ») ; la structure (x, parenthèses, ×) reste fixe.
 */

/**
 * Moins typographique « − » (U+2212) — réservé à la PROSE (« le signe − devant
 * la parenthèse »). Dans un fragment LaTeX, écrire le `-` ASCII : KaTeX le rend
 * déjà comme un vrai signe moins.
 */
export const MOINS = '−';

export const pgcd = (a, b) => { a = Math.abs(a); b = Math.abs(b); while (b) { [a, b] = [b, a % b]; } return a || 1; };
export const rat = (p, q = 1) => { if (q < 0) { p = -p; q = -q; } const g = pgcd(p, q); return { p: p / g, q: q / g }; };
export const ratCmp = (a, b) => a.p * b.q - b.p * a.q;

/** Résout a·x + b = c·x + d → rationnel exact (null si a === c). */
export const solveAffine = (a, b, c, d) => (a === c ? null : rat(d - b, a - c));

/** { 5,1 } → « 5 » ; { -5,13 } → « -5/13 » (signe ASCII, pour les trous). */
export const fmtRat = (r) => (r.q === 1 ? String(r.p) : `${r.p}/${r.q}`);

/**
 * Fragment LaTeX d'une liste de termes [coef, suffixe] (coefs entiers), en
 * cachant les coefficients nuls et les 1/−1 devant une lettre.
 *   fmtPoly([[3,'x'],[5,'']])      → « 3x + 5 »
 *   fmtPoly([[-1,'x^{2}'],[4,'x']]) → « -x^{2} + 4x »
 */
export const fmtPoly = (termes) => {
    const nz = termes.filter(([c]) => c !== 0);
    if (nz.length === 0) return '0';
    let out = '';
    nz.forEach(([c, suf], i) => {
        const abs = Math.abs(c);
        const body = (abs === 1 && suf !== '') ? suf : `${abs}${suf}`;
        if (i === 0) out += (c < 0 ? '-' : '') + body;
        else out += (c < 0 ? ' - ' : ' + ') + body;
    });
    return out;
};

/** Fragment « ax + b » avec une lettre donnée. */
export const fmtAffine = (a, b, L = 'x') => fmtPoly([[a, L], [b, '']]);

/** Fragment binôme parenthésé : « \left(2x - 3\right) ». */
export const fmtBinome = (a, b, L = 'x') => `\\left(${fmtAffine(a, b, L)}\\right)`;

/** Fragment LaTeX du carré d'une lettre : « x^{2} ». */
export const carre = (L = 'x') => `${L}^{2}`;

/** Produit (ax + b)(cx + d) → [A, B, C] du développé Ax² + Bx + C. */
export const mulBinomes = (a, b, c, d) => [a * c, a * d + b * c, b * d];
